import { createGlobalVar, createVar, globalStyle, keyframes, style } from '@vanilla-extract/css';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

export const ringRadius = createVar('gradient-ring-radius');

/** Fill-gradient color stops — overridden for dark mode via globalStyle below. */
const glowFillStopA = createVar('gradient-ring-fill-a');
const glowFillStopB = createVar('gradient-ring-fill-b');

/** Conic-gradient color stops — overridden for dark mode via globalStyle below. */
const conicMist = createVar('gradient-ring-conic-mist');
const conicPeach = createVar('gradient-ring-conic-peach');
const conicViolet = createVar('gradient-ring-conic-violet');

const BASE_GLOW_ROTATION_DEG_PER_SEC = 101;
export const GLOW_ROTATION_DEG_PER_SEC = BASE_GLOW_ROTATION_DEG_PER_SEC * 0.75 * 0.75;

/** JS updates this for rotating conic "glow" border (padding-box + border-box technique). */
export const gradientRingAngleVariable = '--gradient-ring-angle';
const gradientRingAngle = createGlobalVar('gradient-ring-angle', {
  syntax: '<angle>',
  inherits: true,
  initialValue: '0deg',
});

const gradientRingSpin = keyframes(
  { from: { vars: { [gradientRingAngle]: '0deg' } }, to: { vars: { [gradientRingAngle]: '360deg' } } },
  'gradient-ring-spin',
);

/**
 * Stable hook for cross-file Vanilla Extract composition (e.g. Ask Agent sparkle crossfade).
 * Must match the attribute set on `GradientRing`'s root `Box`.
 */
export const gradientRingDataAttribute = 'data-gradient-ring';

/** Selector fragment: hovered ring root while pointer is over the control or its descendants. */
const gradientRingRootHover = `[${gradientRingDataAttribute}]:hover`;

/** Keyboard / focus parity with hover (descendant has focus). */
const gradientRingRootFocusWithin = `[${gradientRingDataAttribute}]:focus-within`;

export const gradientRingActiveAttribute = 'data-gradient-ring-active';

const gradientRingRootActive = `[${gradientRingActiveAttribute}]`;

/** `&` = descendant; use for overlay/sparkle sync when ring chrome is shown. */
export const gradientRingRootGlowChild = `${gradientRingRootHover} &, ${gradientRingRootFocusWithin} &, ${gradientRingRootActive} &`;

const conicGlowStops = `transparent 0deg, transparent 8deg, ${conicMist} 14deg, ${conicPeach} 97deg, ${conicViolet} 180deg, ${conicPeach} 265deg, transparent 352deg`;

const glowFill = `radial-gradient(112.04% 137.09% at 0% 14.29%, ${glowFillStopA} 0%, ${glowFillStopB} 100%)`;

/** Shared with consumers that crossfade in sync with the ring overlay (theme motion tokens). */
export const gradientRingGlowOpacityTransition = `opacity ${themeVars.motion['duration-moderate-01']} ${themeVars.motion['easing-entrance-expressive']}`;

const hoverTargetStateTransition = `background-color ${themeVars.motion['duration-moderate-01']} ${themeVars.motion['easing-standard-expressive']}, color ${themeVars.motion['duration-moderate-01']} ${themeVars.motion['easing-standard-expressive']}, box-shadow ${themeVars.motion['duration-moderate-01']} ${themeVars.motion['easing-standard-expressive']}, border-color ${themeVars.motion['duration-moderate-01']} ${themeVars.motion['easing-standard-expressive']}`;

export const gradientContainer = style(
  {
    vars: {
      [glowFillStopA]: `color-mix(in srgb, ${themeVars.palette['violet-25']} 80%, ${themeVars.color['surface-content']})`,
      [glowFillStopB]: `color-mix(in srgb, ${themeVars.palette['orange-25']} 80%, ${themeVars.color['surface-content']})`,
      [conicMist]: `color-mix(in srgb, ${themeVars.color['scope-neutral-subtle-border']} 40%, transparent)`,
      [conicPeach]: `color-mix(in srgb, ${themeVars.palette['orange-300']} 60%, transparent)`,
      [conicViolet]: `color-mix(in srgb, ${themeVars.palette['violet-500']} 60%, transparent)`,
    },
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    boxSizing: 'border-box',
    borderRadius: ringRadius,
    flexShrink: 0,
    isolation: 'isolate',
    transition: `box-shadow ${themeVars.motion['duration-moderate-01']} ${themeVars.motion['easing-entrance-expressive']}`,
    selectors: {
      '&:hover': {
        boxShadow: `0 0 0 ${themeVars.border['width-0125']} ${themeVars.color['scope-neutral-subtle-border']}`,
      },
      '&:focus-within': {
        boxShadow: `0 0 0 ${themeVars.border['width-0125']} ${themeVars.color['scope-neutral-subtle-border']}`,
      },
    },
    '@media': {
      '(prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
  },
  'gradient-ring',
);

/** Dark-mode overrides — deeper palette stops so the fill doesn't wash out. */
globalStyle(`[data-pluma-color-scheme="dark"] ${gradientContainer}`, {
  vars: {
    [glowFillStopA]: `color-mix(in srgb, ${themeVars.palette['violet-950']} 18%, ${themeVars.color['surface-content']})`,
    [glowFillStopB]: `color-mix(in srgb, ${themeVars.palette['orange-950']} 18%, ${themeVars.color['surface-content']})`,
    [conicPeach]: `color-mix(in srgb, ${themeVars.palette['orange-300']} 50%, transparent)`,
    [conicViolet]: `color-mix(in srgb, ${themeVars.palette['violet-500']} 50%, transparent)`,
  },
});

export const gradientOverlay = style(
  {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    boxSizing: 'border-box',
    borderRadius: ringRadius,
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    border: `${themeVars.border['width-025']} solid transparent`,
    backgroundImage: `${glowFill}, conic-gradient(from calc(${gradientRingAngle} + 180deg), ${conicGlowStops})`,
    backgroundOrigin: 'padding-box, border-box',
    backgroundClip: 'padding-box, border-box',
    transition: `${gradientRingGlowOpacityTransition}, visibility 0s linear ${themeVars.motion['duration-moderate-01']}`,
    selectors: {
      [gradientRingRootGlowChild]: {
        opacity: 1,
        visibility: 'visible',
        transition: gradientRingGlowOpacityTransition,
      },
      [`${gradientRingRootActive} &`]: {
        animation: `${gradientRingSpin} ${360 / GLOW_ROTATION_DEG_PER_SEC}s linear infinite`,
      },
    },
    '@media': {
      '(prefers-reduced-motion: reduce)': {
        transition: 'none',
        selectors: {
          [gradientRingRootGlowChild]: {
            transition: 'none',
          },
          [`${gradientRingRootActive} &`]: {
            animation: 'none',
          },
        },
      },
    },
  },
  'gradient-ring-overlay',
);

/**
 * Child control — transparent surface when ring chrome is active (hover or focus-within).
 * Do not add a visible border here; it paints over the conic ring.
 */
export const gradientHoverTarget = style(
  {
    position: 'relative',
    zIndex: 1,
    transition: hoverTargetStateTransition,
    selectors: {
      [gradientRingRootGlowChild]: {
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        color: themeVars.color['text-primary'],
        border: `${themeVars.border['width-0125']} solid transparent`,
      },
      [`${gradientRingRootHover} &:not(:focus-visible), ${gradientRingRootFocusWithin} &:not(:focus-visible), ${gradientRingRootActive} &:not(:focus-visible)`]:
        {
          boxShadow: 'none',
        },
    },
    '@media': {
      '(prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
  },
  'gradient-ring-hover-target',
);
